import { TContractBase, TContractServiceBE } from './Contract.types';
import { TDashboardInsights, TDashboardScores, TDashboardTrend } from './Dashboard.types';
import { THealthDomain, THealthDomainScores, THealthSubDomain } from './Health.types';

export type TOrganizationContactInfo = {
  name: string;
  website?: string;
  representativeFullName?: string;
  representativePhoneNumber?: string;
  representativeEmail?: string;
};

export type TOrganizationLogo = {
  data: string; // base64
  fileName: string;
};

export type TOrganization = TOrganizationContactInfo &
  TOrganizationLogo & {
    _id: string;
    logoUrl?: string;
    members: string[];
  };

type TOrgMonthlyProgress = {
  month: number;
  year: number;
  averageScore: number;
  screenerCount: number;
};

export enum OrganizationDashboardStatsTrend {
  IMPROVING = 'IMPROVING',
  DECLINING = 'DECLINING',
  STABLE = 'STABLE',
}

export type TOrganizationDashboardStatsItem = {
  current: number;
  previous: number;
  trend: OrganizationDashboardStatsTrend;
};

export type TOrganizationDashboardStats = {
  totalScreeners: TOrganizationDashboardStatsItem;
  totalMembersSignedIn: TOrganizationDashboardStatsItem;
  peerSupportRequests: TOrganizationDashboardStatsItem;
  peerSupportCertifications: {
    current: 0;
    total: 0;
  };
  unknownRisk: number;
  trainingSessions: { completed: number; total: number } | null;
  memberCareSessions: TOrganizationDashboardStatsItem;
  sessionsUtilization: {
    totalUsed: number;
    totalAvailable: number;
    monthly: {
      year: number;
      month: number;
      used: number;
      purchased: number;
    }[];
  };
};

export type TOrganizationDashboardEngagement = {
  signedUp: number;
  invited: number;
  screenerParticipants: { last30Days: number; trend: number };
  screenersTaken: { last30Days: number; trend: number };
};

export type TOrgConcernLevel = {
  percentage: number;
  change: number | null; // e.g. +2 ,-5, null
};
export type TOrganizationDashboardConcernLevels = {
  lowConcern: TOrgConcernLevel;
  someConcern: TOrgConcernLevel;
  highConcern: TOrgConcernLevel;
};

export type TOrganizationDashboardProgress = {
  month: number;
  year: number;
  averageScore: number;
  screenerCount: number;
  screenerCountUnique?: number;
  screenerActivityPercentage?: number;
};

// Backend-derived classification of the org's engagement state (see WholenessObservationService on BO).
// Drives which narrative sentence the Wholeness Observation card renders.
export enum OrganizationEngagementPattern {
  HIGH_ENGAGEMENT_LOW_SIGNUP = 'HIGH_ENGAGEMENT_LOW_SIGNUP', // joiners stay, few invited sign up — onboarding/awareness gap
  HIGH_ENGAGEMENT_HIGH_SIGNUP = 'HIGH_ENGAGEMENT_HIGH_SIGNUP', // healthy on both axes
  LOW_ENGAGEMENT_LOW_SIGNUP = 'LOW_ENGAGEMENT_LOW_SIGNUP', // broken at both ends
  LOW_ENGAGEMENT_HIGH_SIGNUP = 'LOW_ENGAGEMENT_HIGH_SIGNUP', // easy to sign up but no retention
  BALANCED = 'BALANCED', // neither clearly high nor low (rates 51–79)
}

// Domain score on the Wholeness Observation card. NOTE: BO already returns this on the 10–100 scale
// (1–10 source × 10, min 10) — do NOT apply WELLNESS_DATA_MULTIPLIER to it.
export type TWholenessObservationDomainScore = {
  domainName: string; // "Mental Wellness"
  domainKey: THealthDomain; // "mental"
  score: number; // 10–100
};

export type TWholenessObservationEngagementSignals = {
  engagementRate: number; // 0–100 — signed-up members active in last 30 days ÷ signed up
  signUpRate: number; // 0–100 — signed up ÷ invited
  pattern: OrganizationEngagementPattern;
};

// "Wholeness Observation" top-stat block surfaced next to the Wholeness Ring on the org &
// peer-support dashboards. Purely backend-derived — no AI.
export type TWholenessObservation = {
  wholenessScore: number; // 10–100, overall org score
  strengthDomains: TOrganizationReportDomainBreakdownDto[]; // up to 3, highest score first
  opportunityDomains: TOrganizationReportDomainBreakdownDto[]; // up to 3, lowest score first
  engagementSignals: TWholenessObservationEngagementSignals;
};

export type TOrganizationDashboard = {
  organizationId: string;
  name: string;
  logoUrl?: string;
  hasActiveContract: boolean;
  stats: TOrganizationDashboardStats;
  score: TDashboardScores;
  insights: TDashboardInsights;
  trends: TDashboardTrend;
  engagement: TOrganizationDashboardEngagement;
  concernLevels: TOrganizationDashboardConcernLevels;
  progress: TOrganizationDashboardProgress[];
  // Required on GET /organization/dash/dashboard (BO returns it even on the fallback path);
  // optional on the peer-support dashboard responses, which reuse this type.
  wholenessObservation?: TWholenessObservation;
};

export const TEAM_CONCERNS_RANGES = ['last-month', 'last-3-months', 'last-6-months', 'last-year', 'all-time'] as const;
export type TTeamConcernsRange = (typeof TEAM_CONCERNS_RANGES)[number];

export type TTeamConcernCategory = {
  category: THealthSubDomain;
  domain: Uppercase<THealthDomain>;
  peerSupportCount: number;
  screenerCount: number;
  total: number;
};

export type TTeamConcerns = {
  range: TTeamConcernsRange;
  categories: TTeamConcernCategory[];
};

export type TOrganizationActiveContract = TContractBase & {
  adminName: string;
  phone: string;
  adminEmail: string;
  services: TContractServiceBE[];
  history?: any[]; // not needed for now

  // added by me; non-existent on BO
  updatedAt: string;
  updatedBy: string;
};

// deprecated - May 19th 2025
export type TOrganizationDashboardOld = {
  organizationId: string;
  name: string;
  logoUrl: string;
  score: THealthDomainScores & { totalScore: number };
  engagement: {
    signedUp: number;
    invited: number;
    screenerParticipants: {
      last30Days: number;
      trend: string;
    };
    screenersTaken: {
      last30Days: number;
      completionRatio: number;
      trend: string;
    };
  };
  concernLevels: {
    lowConcern: TOrgConcernLevel;
    someConcern: TOrgConcernLevel;
    highConcern: TOrgConcernLevel;
  };
  progress: TOrgMonthlyProgress[];
};

export type OrganizationReportSubdomainDto = {
  name: string;
  score: number;
};

export type TOrganizationReportDomainBreakdownDto = {
  domainName: string;
  domainKey: string;
  score: number;
  subdomains: OrganizationReportSubdomainDto[];
};

export type TOrganizationReport = {
  organizationId: string;
  organizationName: string;
  dataPeriod: {
    month: number;
    year: number;
  };
  wholenessScore: {
    current: number;
    previous?: number;
    changePercent?: number;
    direction: 'UP' | 'DOWN' | 'FLAT';
  };
  rosterStats: {
    employeesInRoster: number;
    screenersSubmitted: number;
    totalSubmitters: number;
    peerSupportMembers: number;
    clinicalConsultationSessions: number;
  };
  teamStrengths: {
    previousMonth: TOrganizationReportDomainBreakdownDto[];
    currentMonth: TOrganizationReportDomainBreakdownDto[];
  };
  opportunitiesForGrowth: {
    previousMonth: TOrganizationReportDomainBreakdownDto[];
    currentMonth: TOrganizationReportDomainBreakdownDto[];
  };
  generatedAt: string;
};
